/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {PositionStrategy} from './position-strategy';
import {ElementRef} from '@angular/core';
import {ViewportRuler} from './viewport-ruler';
import {
  ConnectionPositionPair,
  OriginConnectionPosition,
  OverlayConnectionPosition,
  ConnectedOverlayPositionChange, ScrollableViewProperties
} from './connected-position';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import {Scrollable} from '../scroll/scrollable';

/**
 * Container to hold the bounding positions of a particular element with respect to the viewport,
 * where top and bottom are the y-axis coordinates of the bounding rectangle and left and right are
 * the x-axis coordinates.
 */
type ElementBoundingPositions = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};
/**
 * A strategy for positioning overlays. Using this strategy, an overlay is given an
 * implicit position relative some origin element. The relative position is defined in terms of
 * a point on the origin element that is connected to a point on the overlay element. For example,
 * a basic dropdown is connecting the bottom-left corner of the origin to the top-left corner
 * of the overlay.
 */
export class ConnectedPositionStrategy implements PositionStrategy {
private _dir = 'ltr';
/**
 * The offset in pixels for the overlay connection point on the x-axis
 */
private _offsetX: number = 0;
/**
 * The offset in pixels for the overlay connection point on the y-axis
 */
private _offsetY: number = 0;
/**
 * The Scrollable containers used to check scrollable view properties on position change.
 */
private scrollables: Scrollable[] = [];
/**
 * Whether the we're dealing with an RTL context
 * @return {?}
 */
get _isRtl() {
    return this._dir === 'rtl';
  }
/**
 * Ordered list of preferred positions, from most to least desirable.
 */
_preferredPositions: ConnectionPositionPair[] = [];
/**
 * The origin element against which the overlay will be positioned.
 */
private _origin: HTMLElement;
/**
 * The overlay pane element.
 */
private _pane: HTMLElement;
/**
 * The last position to have been calculated as the best fit position.
 */
private _lastConnectedPosition: ConnectionPositionPair;

  _onPositionChange:
      Subject<ConnectedOverlayPositionChange> = new Subject<ConnectedOverlayPositionChange>();
/**
 * Emits an event when the connection point changes.
 * @return {?}
 */
get onPositionChange(): Observable<ConnectedOverlayPositionChange> {
    return this._onPositionChange.asObservable();
  }
/**
 * @param {?} _connectedTo
 * @param {?} _originPos
 * @param {?} _overlayPos
 * @param {?} _viewportRuler
 */
constructor(
private _connectedTo: ElementRef,
private _originPos: OriginConnectionPosition,
private _overlayPos: OverlayConnectionPosition,
private _viewportRuler: ViewportRuler) {
    this._origin = this._connectedTo.nativeElement;
    this.withFallbackPosition(_originPos, _overlayPos);
  }
/**
 * Ordered list of preferred positions, from most to least desirable.
 * @return {?}
 */
get positions() {
    return this._preferredPositions;
  }
/**
 * To be used to for any cleanup after the element gets destroyed.
 * @return {?}
 */
dispose() { }
/**
 * Updates the position of the overlay element, using whichever preferred position relative
 * to the origin fits on-screen.
 * \@docs-private 
 * 
 * @param {?} element Element to which to apply the CSS styles.
 * @return {?} Resolves when the styles have been applied.
 */
apply(element: HTMLElement): Promise<void> {
    // Cache the overlay pane element in case re-calculating position is necessary
    this._pane = element;

    // We need the bounding rects for the origin and the overlay to determine how to position
    // the overlay relative to the origin.
    const /** @type {?} */ originRect = this._origin.getBoundingClientRect();
    const /** @type {?} */ overlayRect = element.getBoundingClientRect();

    // We use the viewport rect to determine whether a position would go off-screen.
    const /** @type {?} */ viewportRect = this._viewportRuler.getViewportRect();

    // Fallback point if none of the fallbacks fit into the viewport.
    let /** @type {?} */ fallbackPoint: OverlayPoint = null;
    let /** @type {?} */ fallbackPosition: ConnectionPositionPair = null;

    // We want to place the overlay in the first of the preferred positions such that the
    // overlay fits on-screen.
    for (let /** @type {?} */ pos of this._preferredPositions) {
      // Get the (x, y) point of connection on the origin, and then use that to get the
      // (top, left) coordinate for the overlay at `pos`.
      let /** @type {?} */ originPoint = this._getOriginConnectionPoint(originRect, pos);
      let /** @type {?} */ overlayPoint = this._getOverlayPoint(originPoint, overlayRect, viewportRect, pos);

      // If the overlay in the calculated position fits on-screen, put it there and we're done.
      if (overlayPoint.fitsInViewport) {
        this._setElementPosition(element, overlayRect, overlayPoint, pos);

        // Save the last connected position in case the position needs to be re-calculated.
        this._lastConnectedPosition = pos;

        // Notify that the position has been changed along with its change properties.
        const /** @type {?} */ scrollableViewProperties = this.getScrollableViewProperties(element);
        const /** @type {?} */ positionChange = new ConnectedOverlayPositionChange(pos, scrollableViewProperties);
        this._onPositionChange.next(positionChange);

        return Promise.resolve(null);
      } else if (!fallbackPoint || fallbackPoint.visibleArea < overlayPoint.visibleArea) {
        fallbackPoint = overlayPoint;
        fallbackPosition = pos;
      }
    }

    // If none of the preferred positions were in the viewport, take the one
    // with the largest visible area.
    this._setElementPosition(element, overlayRect, fallbackPoint, fallbackPosition);

    return Promise.resolve(null);
  }
/**
 * This re-aligns the overlay element with the trigger in its last calculated position,
 * even if a position higher in the "preferred positions" list would now fit. This
 * allows one to re-align the panel without changing the orientation of the panel.
 * @return {?}
 */
recalculateLastPosition(): void {
    const /** @type {?} */ originRect = this._origin.getBoundingClientRect();
    const /** @type {?} */ overlayRect = this._pane.getBoundingClientRect();
    const /** @type {?} */ viewportRect = this._viewportRuler.getViewportRect();
    const /** @type {?} */ lastPosition = this._lastConnectedPosition || this._preferredPositions[0];

    let /** @type {?} */ originPoint = this._getOriginConnectionPoint(originRect, lastPosition);
    let /** @type {?} */ overlayPoint = this._getOverlayPoint(originPoint, overlayRect, viewportRect, lastPosition);
    this._setElementPosition(this._pane, overlayRect, overlayPoint, lastPosition);
  }
/**
 * Sets the list of Scrollable containers that host the origin element so that
 * on reposition we can evaluate if it or the overlay has been clipped or outside view. Every
 * Scrollable must be an ancestor element of the strategy's origin element.
 * @param {?} scrollables
 * @return {?}
 */
withScrollableContainers(scrollables: Scrollable[]) {
    this.scrollables = scrollables;
  }
/**
 * Adds a new preferred fallback position.
 * @param {?} originPos
 * @param {?} overlayPos
 * @return {?}
 */
withFallbackPosition(
      originPos: OriginConnectionPosition,
      overlayPos: OverlayConnectionPosition): this {
    this._preferredPositions.push(new ConnectionPositionPair(originPos, overlayPos));
    return this;
  }
/**
 * Sets the layout direction so the overlay's position can be adjusted to match.
 * @param {?} dir New layout direction.
 * @return {?}
 */
withDirection(dir: 'ltr' | 'rtl'): this {
    this._dir = dir;
    return this;
  }
/**
 * Sets an offset for the overlay's connection point on the x-axis
 * @param {?} offset New offset in the X axis.
 * @return {?}
 */
withOffsetX(offset: number): this {
    this._offsetX = offset;
    return this;
  }
/**
 * Sets an offset for the overlay's connection point on the y-axis
 * @param {?} offset New offset in the Y axis.
 * @return {?}
 */
withOffsetY(offset: number): this {
    this._offsetY = offset;
    return this;
  }
/**
 * Gets the horizontal (x) "start" dimension based on whether the overlay is in an RTL context.
 * @param {?} rect
 * @return {?}
 */
private _getStartX(rect: ClientRect): number {
    return this._isRtl ? rect.right : rect.left;
  }
/**
 * Gets the horizontal (x) "end" dimension based on whether the overlay is in an RTL context.
 * @param {?} rect
 * @return {?}
 */
private _getEndX(rect: ClientRect): number {
    return this._isRtl ? rect.left : rect.right;
  }
/**
 * Gets the (x, y) coordinate of a connection point on the origin based on a relative position.
 * @param {?} originRect
 * @param {?} pos
 * @return {?}
 */
private _getOriginConnectionPoint(originRect: ClientRect, pos: ConnectionPositionPair): Point {
    const /** @type {?} */ originStartX = this._getStartX(originRect);
    const /** @type {?} */ originEndX = this._getEndX(originRect);

    let /** @type {?} */ x: number;
    if (pos.originX == 'center') {
      x = originStartX + (originRect.width / 2);
    } else {
      x = pos.originX == 'start' ? originStartX : originEndX;
    }

    let /** @type {?} */ y: number;
    if (pos.originY == 'center') {
      y = originRect.top + (originRect.height / 2);
    } else {
      y = pos.originY == 'top' ? originRect.top : originRect.bottom;
    }

    return {x, y};
  }
/**
 * Gets the (x, y) coordinate of the top-left corner of the overlay given a given position and
 * origin point to which the overlay should be connected, as well as how much of the element
 * would be inside the viewport at that position.
 * @param {?} originPoint
 * @param {?} overlayRect
 * @param {?} viewportRect
 * @param {?} pos
 * @return {?}
 */
private _getOverlayPoint(
      originPoint: Point,
      overlayRect: ClientRect,
      viewportRect: ClientRect,
      pos: ConnectionPositionPair): OverlayPoint {
    // Calculate the (overlayStartX, overlayStartY), the start of the potential overlay position
    // relative to the origin point.
    let /** @type {?} */ overlayStartX: number;
    if (pos.overlayX == 'center') {
      overlayStartX = -overlayRect.width / 2;
    } else if (pos.overlayX === 'start') {
      overlayStartX = this._isRtl ? -overlayRect.width : 0;
    } else {
      overlayStartX = this._isRtl ? 0 : -overlayRect.width;
    }

    let /** @type {?} */ overlayStartY: number;
    if (pos.overlayY == 'center') {
      overlayStartY = -overlayRect.height / 2;
    } else {
      overlayStartY = pos.overlayY == 'top' ? 0 : -overlayRect.height;
    }

    // The (x, y) coordinates of the overlay.
    let /** @type {?} */ x = originPoint.x + overlayStartX + this._offsetX;
    let /** @type {?} */ y = originPoint.y + overlayStartY + this._offsetY;

    // How much the overlay would overflow at this position, on each side.
    let /** @type {?} */ leftOverflow = 0 - x;
    let /** @type {?} */ rightOverflow = (x + overlayRect.width) - viewportRect.width;
    let /** @type {?} */ topOverflow = 0 - y;
    let /** @type {?} */ bottomOverflow = (y + overlayRect.height) - viewportRect.height;

    // Visible parts of the element on each axis.
    let /** @type {?} */ visibleWidth = this._subtractOverflows(overlayRect.width, leftOverflow, rightOverflow);
    let /** @type {?} */ visibleHeight = this._subtractOverflows(overlayRect.height, topOverflow, bottomOverflow);

    // The area of the element that's within the viewport.
    let /** @type {?} */ visibleArea = visibleWidth * visibleHeight;
    let /** @type {?} */ fitsInViewport = (overlayRect.width * overlayRect.height) === visibleArea;

    return {x, y, fitsInViewport, visibleArea};
  }
/**
 * Gets the view properties of the trigger and overlay, including whether they are clipped
 * or completely outside the view of any of the strategy's scrollables.
 * @param {?} overlay
 * @return {?}
 */
private getScrollableViewProperties(overlay: HTMLElement): ScrollableViewProperties {
    const /** @type {?} */ originBounds = this._getElementBounds(this._origin);
    const /** @type {?} */ overlayBounds = this._getElementBounds(overlay);
    const /** @type {?} */ scrollContainerBounds = this.scrollables.map((scrollable: Scrollable) => {
      return this._getElementBounds(scrollable.getElementRef().nativeElement);
    });

    return {
      isOriginClipped: this.isElementClipped(originBounds, scrollContainerBounds),
      isOriginOutsideView: this.isElementOutsideView(originBounds, scrollContainerBounds),
      isOverlayClipped: this.isElementClipped(overlayBounds, scrollContainerBounds),
      isOverlayOutsideView: this.isElementOutsideView(overlayBounds, scrollContainerBounds),
    };
  }
/**
 * Whether the element is completely out of the view of any of the containers.
 * @param {?} elementBounds
 * @param {?} containersBounds
 * @return {?}
 */
private isElementOutsideView(
      elementBounds: ElementBoundingPositions,
      containersBounds: ElementBoundingPositions[]): boolean {
    return containersBounds.some((containerBounds: ElementBoundingPositions) => {
      const /** @type {?} */ outsideAbove = elementBounds.bottom < containerBounds.top;
      const /** @type {?} */ outsideBelow = elementBounds.top > containerBounds.bottom;
      const /** @type {?} */ outsideLeft = elementBounds.right < containerBounds.left;
      const /** @type {?} */ outsideRight = elementBounds.left > containerBounds.right;

      return outsideAbove || outsideBelow || outsideLeft || outsideRight;
    });
  }
/**
 * Whether the element is clipped by any of the containers.
 * @param {?} elementBounds
 * @param {?} containersBounds
 * @return {?}
 */
private isElementClipped(
      elementBounds: ElementBoundingPositions,
      containersBounds: ElementBoundingPositions[]): boolean {
    return containersBounds.some((containerBounds: ElementBoundingPositions) => {
      const /** @type {?} */ clippedAbove = elementBounds.top < containerBounds.top;
      const /** @type {?} */ clippedBelow = elementBounds.bottom > containerBounds.bottom;
      const /** @type {?} */ clippedLeft = elementBounds.left < containerBounds.left;
      const /** @type {?} */ clippedRight = elementBounds.right > containerBounds.right;

      return clippedAbove || clippedBelow || clippedLeft || clippedRight;
    });
  }
/**
 * Physically positions the overlay element to the given coordinate.
 * @param {?} element
 * @param {?} overlayRect
 * @param {?} overlayPoint
 * @param {?} pos
 * @return {?}
 */
private _setElementPosition(
      element: HTMLElement,
      overlayRect: ClientRect,
      overlayPoint: Point,
      pos: ConnectionPositionPair) {

    // We want to set either `top` or `bottom` based on whether the overlay wants to appear above
    // or below the origin and the direction in which the element will expand.
    let /** @type {?} */ verticalStyleProperty = pos.overlayY === 'bottom' ? 'bottom' : 'top';

    // When using `bottom`, we adjust the y position such that it is the distance
    // from the bottom of the viewport rather than the top.
    let /** @type {?} */ y = verticalStyleProperty === 'top' ?
        overlayPoint.y :
        document.documentElement.clientHeight - (overlayPoint.y + overlayRect.height);

    // We want to set either `left` or `right` based on whether the overlay wants to appear "before"
    // or "after" the origin, which determines the direction in which the element will expand.
    // For the horizontal axis, the meaning of "before" and "after" change based on whether the
    // page is in RTL or LTR.
    let /** @type {?} */ horizontalStyleProperty: string;
    if (this._dir === 'rtl') {
      horizontalStyleProperty = pos.overlayX === 'end' ? 'left' : 'right';
    } else {
      horizontalStyleProperty = pos.overlayX === 'end' ? 'right' : 'left';
    }

    // When we're setting `right`, we adjust the x position such that it is the distance
    // from the right edge of the viewport rather than the left edge.
    let /** @type {?} */ x = horizontalStyleProperty === 'left' ?
      overlayPoint.x :
      document.documentElement.clientWidth - (overlayPoint.x + overlayRect.width);


    // Reset any existing styles. This is necessary in case the preferred position has
    // changed since the last `apply`.
    ['top', 'bottom', 'left', 'right'].forEach(p => element.style[p] = null);

    element.style[verticalStyleProperty] = `${y}px`;
    element.style[horizontalStyleProperty] = `${x}px`;
  }
/**
 * Returns the bounding positions of the provided element with respect to the viewport.
 * @param {?} element
 * @return {?}
 */
private _getElementBounds(element: HTMLElement): ElementBoundingPositions {
    const /** @type {?} */ boundingClientRect = element.getBoundingClientRect();
    return {
      top: boundingClientRect.top,
      right: boundingClientRect.left + boundingClientRect.width,
      bottom: boundingClientRect.top + boundingClientRect.height,
      left: boundingClientRect.left
    };
  }
/**
 * Subtracts the amount that an element is overflowing on an axis from it's length.
 * @param {?} length
 * @param {...?} overflows
 * @return {?}
 */
private _subtractOverflows(length: number, ...overflows: number[]): number {
    return overflows.reduce((currentValue: number, currentOverflow: number) => {
      return currentValue - Math.max(currentOverflow, 0);
    }, length);
  }
}

function ConnectedPositionStrategy_tsickle_Closure_declarations() {
/** @type {?} */
ConnectedPositionStrategy.prototype._dir;
/**
 * The offset in pixels for the overlay connection point on the x-axis
 * @type {?}
 */
ConnectedPositionStrategy.prototype._offsetX;
/**
 * The offset in pixels for the overlay connection point on the y-axis
 * @type {?}
 */
ConnectedPositionStrategy.prototype._offsetY;
/**
 * The Scrollable containers used to check scrollable view properties on position change.
 * @type {?}
 */
ConnectedPositionStrategy.prototype.scrollables;
/**
 * Ordered list of preferred positions, from most to least desirable.
 * @type {?}
 */
ConnectedPositionStrategy.prototype._preferredPositions;
/**
 * The origin element against which the overlay will be positioned.
 * @type {?}
 */
ConnectedPositionStrategy.prototype._origin;
/**
 * The overlay pane element.
 * @type {?}
 */
ConnectedPositionStrategy.prototype._pane;
/**
 * The last position to have been calculated as the best fit position.
 * @type {?}
 */
ConnectedPositionStrategy.prototype._lastConnectedPosition;
/** @type {?} */
ConnectedPositionStrategy.prototype._onPositionChange;
/** @type {?} */
ConnectedPositionStrategy.prototype._connectedTo;
/** @type {?} */
ConnectedPositionStrategy.prototype._originPos;
/** @type {?} */
ConnectedPositionStrategy.prototype._overlayPos;
/** @type {?} */
ConnectedPositionStrategy.prototype._viewportRuler;
}


/** A simple (x, y) coordinate. */
interface Point {
  x: number;
  y: number;
}

/**
 * Expands the simple (x, y) coordinate by adding info about whether the
 * element would fit inside the viewport at that position, as well as
 * how much of the element would be visible.
 */
interface OverlayPoint extends Point {
  visibleArea?: number;
  fitsInViewport?: boolean;
}
